import { apiFetch } from "./client";
import type {
  ContrattiListResponse,
  ClientiListResponse,
  DashboardAgenziaResponse,
  DettaglioPraticaAgenzia,
  DettaglioClienteAgenzia,
  BollettaApiDetail,
  ModuloTipo,
} from "@/types";

export async function getDashboardAgenzia(): Promise<DashboardAgenziaResponse> {
  return apiFetch<DashboardAgenziaResponse>("/espo/agenzia/dashboard");
}

export async function getPraticheAgenzia(params: {
  limit?: number;
  offset?: number;
  stage?: string | null;
  q?: string;
}): Promise<ContrattiListResponse> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  if (params.stage) qs.set("stage", params.stage);
  if (params.q) qs.set("q", params.q);
  return apiFetch<ContrattiListResponse>(`/espo/agenzia/pratiche?${qs.toString()}`);
}

export async function getPraticaAgenzia(id: string): Promise<DettaglioPraticaAgenzia> {
  return apiFetch<DettaglioPraticaAgenzia>(`/espo/agenzia/pratiche/${id}`);
}

export async function getClientiAgenzia(params: {
  limit?: number;
  offset?: number;
  q?: string;
}): Promise<ClientiListResponse> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  if (params.q) qs.set("q", params.q);
  return apiFetch<ClientiListResponse>(`/espo/agenzia/clienti?${qs.toString()}`);
}

export async function getClienteAgenzia(id: string): Promise<DettaglioClienteAgenzia> {
  return apiFetch<DettaglioClienteAgenzia>(`/espo/agenzia/clienti/${id}`);
}

export function getBollettaAgenzia(id: string): Promise<BollettaApiDetail> {
  return apiFetch<BollettaApiDetail>(`/espo/agenzia/bollette/${encodeURIComponent(id)}`);
}

export async function deletePraticaAgenzia(id: string): Promise<void> {
  return apiFetch<void>(`/espo/agenzia/pratiche/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export interface ModuloGenerato {
  tipo: string;
  filename: string;
  url: string;
}

export async function generaModuliPratica(id: string): Promise<{ moduli: ModuloGenerato[] }> {
  return apiFetch<{ moduli: ModuloGenerato[] }>(
    `/espo/agenzia/pratiche/${encodeURIComponent(id)}/moduli/genera`,
    { method: "POST" },
  );
}

type ModuloFirmatoOut = { id: string; name: string; tipo: ModuloTipo | null };

// stato_invio: esito dell'hook auto-invio A2A dopo l'operazione ("invio in partenza" |
// "in coda, parte alle 09:00" | "non abilitato" | "non inviabile: <...>" | ""). Il backend
// lo valorizza solo dopo upload (delete/patch tornano sempre "").
export interface ModuliFirmatiResponse {
  moduli_firmati: ModuloFirmatoOut[];
  stato_invio: string;
}

export async function uploadModuliFirmati(
  id: string,
  files: File[],
): Promise<ModuliFirmatiResponse> {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return apiFetch(`/espo/agenzia/pratiche/${encodeURIComponent(id)}/moduli-firmati`, {
    method: "POST",
    body: fd,
  });
}

export async function deleteModuloFirmato(
  id: string,
  attachmentId: string,
): Promise<ModuliFirmatiResponse> {
  return apiFetch(
    `/espo/agenzia/pratiche/${encodeURIComponent(id)}/moduli-firmati/${encodeURIComponent(attachmentId)}`,
    { method: "DELETE" },
  );
}

export async function setTipoModuloFirmato(
  id: string,
  attachmentId: string,
  tipo: ModuloTipo,
): Promise<ModuliFirmatiResponse> {
  return apiFetch(
    `/espo/agenzia/pratiche/${encodeURIComponent(id)}/moduli-firmati/${encodeURIComponent(attachmentId)}`,
    { method: "PATCH", body: JSON.stringify({ tipo }) },
  );
}

export function urlDownloadModuloFirmato(id: string, attachmentId: string): string {
  return `/api/espo/agenzia/pratiche/${encodeURIComponent(id)}/moduli-firmati/${encodeURIComponent(attachmentId)}/file`;
}

export function resendPrivacyLink(contactId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/espo/agenzia/clienti/${encodeURIComponent(contactId)}/privacy-link`,
    { method: "POST" },
  );
}

export interface PrivacyStatus {
  informativa_consegnata: boolean;
  presa_visione: boolean;
  marketing: boolean | null;
  marketing_telefono: boolean | null;
}

export function getPrivacyStatus(contactId: string): Promise<PrivacyStatus> {
  return apiFetch<PrivacyStatus>(
    `/espo/agenzia/clienti/${encodeURIComponent(contactId)}/privacy-status`,
  );
}
